import {inject, Injectable} from '@angular/core';
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {SecurityUtils} from "../security.utils";
import {catchError} from "rxjs/operators";
import {HttpClientUtils} from "../../utils/http-client.utils";
import {ApplicationGeneric} from "../model/new-application.model";

@Injectable({
  providedIn: 'root'
})
export class FunctionalCapacitiesService {
  private backendUrl = (tenantId: string) => `${environment.engineURL}/tenants/${tenantId}/functional-capacity`;
  private httpClient = inject(HttpClient);

  getTenantFunctionalCapacities(tenantId: string): Observable<FunctionalCapacity[]> {
	  return this.httpClient.get<FunctionalCapacity>(`${this.backendUrl(tenantId)}`, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  getTenantFunctionalCapacitiesByApplicationId(tenantId: string, applicationId: string): Observable<FunctionalCapacity[]> {
	  return this.httpClient.get<FunctionalCapacity>(`${this.backendUrl(tenantId)}/applications/${applicationId}`, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  deleteFunctionalCapacities(tenantId: string, body: FunctionalCapacityForm): Observable<boolean> {
	  return this.httpClient.post<boolean>(`${this.backendUrl(tenantId)}/delete`, body, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  createFunctionalCapacityGroup(tenantId: string, body: FunctionalCapacityGroupCreateForm): Observable<FunctionalCapacity | null> {
	  return this.httpClient.post<FunctionalCapacity>(`${this.backendUrl(tenantId)}/group`, body, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  createFunctionalCapacity(tenantId: string, body: FunctionalCapacityCreateForm): Observable<FunctionalCapacity | null> {
	  return this.httpClient.post<FunctionalCapacity>(`${this.backendUrl(tenantId)}`, body, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  updateFunctionalCapacity(tenantId: string, functionalCapacityId: string, body: FunctionalCapacityUpdateForm): Observable<boolean> {
	  return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/${functionalCapacityId}`, body, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  updateFunctionalCapacityGroup(tenantId: string, functionalCapacityGroupId: string, body: FunctionalCapacityGroupUpdateForm): Observable<boolean> {
	  return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/group/${functionalCapacityGroupId}`, body, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  updateApplicationFunctionalCapacityList(tenantId: string, applicationId: string, body: FunctionalCapacityForm): Observable<boolean> {
	  return this.httpClient.post<boolean>(`${this.backendUrl(tenantId)}/applications/${applicationId}`, body, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }

  getFunctionalCapacityDiagram(tenantId: string): Observable<FunctionalCapacityGroupDiagram[]> {
	  return this.httpClient.get<FunctionalCapacityGroupDiagram[]>(`${this.backendUrl(tenantId)}/diagram`, {
		  headers: SecurityUtils.getAuthHttpHeaders(),
	  }).pipe(catchError(error => HttpClientUtils.handleError(error, null)));
  }
}

export interface FunctionalCapacity {
	id: string,
	name: string,
	icon: string | null,
	children: FunctionalCapacity[],
	expanded?: boolean
}

export interface FunctionalCapacityForm {
	functionalCapacityIds: string[],
}

export interface FunctionalCapacityGroupCreateForm {
	name: string,
	icon: FunctionalCapacityIcon
}

export interface FunctionalCapacityCreateForm {
	groupId: string,
	name: string,
}

export interface FunctionalCapacityUpdateForm {
	name: string
}

export interface FunctionalCapacityGroupUpdateForm {
	name: string,
	icon: FunctionalCapacityIcon
}

export enum FunctionalCapacityIcon {
	PACKAGE = 'package',
	COINS_STACKED_01 = 'coins-stacked-01',
	ACTIVITY_HEART = 'activity-heart',
	TICKET_01 = 'ticket-01',
	USERS_01 = 'users-01',
	RECEIPT_CHECK = 'receipt-check',
	TOOL_02 = 'tool-02',
	BAR_LINE_CHART = 'bar-line-chart',
	COINS_HAND = 'coins-hand',
	ATOM_02 = 'atom-02',
	BEAKER_02 = 'beaker-02',
	MESSAGE_CHAT_SQUARE = 'message-chat-square',
	CPU_CHIP_01 = 'cpu-chip-01',
	COMPASS = 'compass',
	SHIELD_TICK = 'shield-tick',
	ANNOUNCEMENT_01 = 'announcement-01',
	COMPASS_01 = 'compass-01',
	TRUCK_01 = 'truck-01',
	GRADUATION_HAT_02 = 'graduation-hat-02',
	PIE_CHART_03 = 'pie-chart-03',
	GIFT_01 = 'gift-01',
	LIGHTBULB_02 = 'lightbulb-02',
	MICROPHONE_01 = 'microphone-01',
	AWARD_03 = 'award-03',
	BUILDING_05 = 'building-05',
	ROCKET_02 = 'rocket-02',
	DATA = 'data',
	ALERT_TRIANGLE = 'alert-triangle'
}

export interface FunctionalCapacityGroupDiagram {
	id: string,
	name: string,
	icon: string,
	children: FunctionalCapacityChildrenDiagram[]
}

export interface FunctionalCapacityChildrenDiagram {
	id: string,
	name: string,
	applications: ApplicationGeneric[]
}
